import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { OrganisationMember } from 'PublicEntityTypes';
import { Stack } from '../../../design-system/stack/stack';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../design-system/table/table';
import { cn } from '../../../utils/cn';
import { getInviteStatusCell, getMemberCell, getRoleCell, inviteStatusSortFn } from '../utils';

const columns: (loggedInUserEmail?: string) => ColumnDef<OrganisationMember>[] = (loggedInUserEmail) => [
  {
    accessorFn: (row) => row.email === loggedInUserEmail,
    id: 'isLoggedInUser',
  },
  {
    accessorKey: 'email',
    id: 'email',
    header: 'Member',
    cell: ({ row }) =>
      getMemberCell(row.original.email, row.original.firstName, row.original.lastName, row.original.pictureUrl),
    sortingFn: 'text',
  },
  {
    accessorKey: 'role',
    id: 'role',
    header: 'Role',
    cell: () => getRoleCell('admin'), // TODO: Add role from API
    sortingFn: 'text',
  },
  {
    accessorKey: 'status',
    id: 'status',
    cell: ({ row }) => getInviteStatusCell(row.original.status, row.original.email, loggedInUserEmail),
    sortingFn: inviteStatusSortFn,
  },
];

export type MembersTableProps = {
  members: OrganisationMember[];
  loggedInUserEmail: string;
};

export const MembersTable = ({ members, loggedInUserEmail }: MembersTableProps) => {
  const table = useReactTable({
    data: members,
    columns: columns(loggedInUserEmail),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    initialState: {
      columnVisibility: {
        isLoggedInUser: false,
      },
      sorting: [
        { id: 'isLoggedInUser', desc: true },
        { id: 'status', desc: false },
        { id: 'email', desc: false },
      ],
      pagination: {
        pageSize: 1000,
      },
    },
  });

  return (
    <>
      <Table>
        <TableHeader className="text-xs text-muted-foreground">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className={`${header.column.getCanSort() && header.id !== 'status' ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    <div
                      onClick={
                        header.column.getCanSort() && header.id !== 'status'
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                      className="flex items-center gap-1.5"
                    >
                      {header.isPlaceholder || header.id === 'status'
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && header.id !== 'status' && (
                        <Stack className="">
                          <ChevronUpIcon
                            className={`-mb-0.5 h-3 w-3 ${header.column.getIsSorted() === 'asc' ? 'text-black' : 'text-muted-foreground'}`}
                          />
                          <ChevronDownIcon
                            className={`-mt-0.5 h-3 w-3 ${header.column.getIsSorted() === 'desc' ? 'text-black' : 'text-muted-foreground'}`}
                          />
                        </Stack>
                      )}
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody className="text-sm text-black">
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, index) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                className={cn(index % 2 !== 0 ? 'bg-muted/50' : '')}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24">
                No members.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};
